import type { DrrViewRow, InventoryAgeBuckets, InventoryAgeingRow } from '../../api/inventory';
import { leafRowsOf } from '../../utils/rowTree';

/** Mirrors the backend default; the response's `bucket_edges` is what actually applies. */
export const DEFAULT_AGEING_EDGES = [30, 60, 90, 180];

/** The "old stock" line the summary and the share column report against. */
export const OVER_THRESHOLD_DAYS = 90;

/** '0–30', '31–60', …, '180+' for edges [30, 60, …, 180]. */
export const ageBucketLabels = (edges: number[]): string[] => [
    ...edges.map((edge, index) => (index === 0 ? `0–${edge}` : `${edges[index - 1] + 1}–${edge}`)),
    `${edges[edges.length - 1] ?? 0}+`,
];

export const datedUnits = (row: InventoryAgeingRow): number =>
    (row.internal.stock ?? 0) - row.age.older_than_history;

/**
 * Units in every bucket that starts after `thresholdDays`. Bucket `i > 0` starts at
 * `edges[i - 1] + 1`, so it counts when `edges[i - 1] >= thresholdDays`.
 */
export const unitsOlderThan = (
    age: InventoryAgeBuckets,
    edges: number[],
    thresholdDays: number,
): number =>
    age.units.reduce(
        (sum, units, index) =>
            index > 0 && (edges[index - 1] ?? 0) >= thresholdDays ? sum + units : sum,
        0,
    );

/**
 * A group's age from its member rows: buckets sum, weighted age is recomputed as
 * Σ(age × dated units) / Σ(dated units), never averaged, and the oldest receipt is the min.
 */
export const aggregateAge = (
    rows: InventoryAgeingRow[],
    bucketCount: number,
): InventoryAgeBuckets => {
    const units = new Array<number>(bucketCount).fill(0);
    let olderThanHistory = 0;
    let weightedSum = 0;
    let dated = 0;
    let oldest: string | null = null;
    for (const row of rows) {
        row.age.units.forEach((value, index) => {
            if (index < bucketCount) units[index] += value;
        });
        olderThanHistory += row.age.older_than_history;
        const rowDated = datedUnits(row);
        if (row.age.weighted_age_days !== null && rowDated > 0) {
            weightedSum += row.age.weighted_age_days * rowDated;
            dated += rowDated;
        }
        const date = row.age.oldest_receipt_date;
        if (date && (oldest === null || date < oldest)) oldest = date;
    }
    return {
        units,
        older_than_history: olderThanHistory,
        weighted_age_days: dated > 0 ? weightedSum / dated : null,
        oldest_receipt_date: oldest,
    };
};

/**
 * `rollupDrrRows` builds the group rows (and sums stock / DRR for them); this gives each
 * group its age from its leaves, at every nesting level.
 */
export const attachAgeAggregates = (
    rows: DrrViewRow[],
    bucketCount: number,
): InventoryAgeingRow[] =>
    rows.map((row) => {
        if (!row.subRows?.length) return row as InventoryAgeingRow;
        const subRows = attachAgeAggregates(row.subRows, bucketCount);
        return {
            ...row,
            subRows,
            age: aggregateAge(leafRowsOf(subRows), bucketCount),
        } as InventoryAgeingRow;
    });
